import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, Badge, DataTable, Loading, formatCurrency, formatDate, ouvrirDocument, telechargerDocument, BackButton, styleMessage } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

function useCategories() {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    apiFetch('/formation-categories').then(d => setCategories((d || []).map(c => c.libelle))).catch(() => {});
  }, []);
  return categories;
}

function useFormations() {
  const [formations, setFormations] = useState([]);
  useEffect(() => {
    apiFetch('/formations').then(d => setFormations((d.data || d || []).filter(f => f.actif !== false))).catch(() => {});
  }, []);
  return formations;
}

function ModalModifier({ formation, onSave, onClose }) {
  const [form, setForm] = useState({ ...formation });
  const categories = useCategories();
  const formations = useFormations();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [numeroError, setNumeroError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function validateNumero(val) {
    if (!val || !String(val).trim()) return 'Le N° de formation est obligatoire';
    return /^\d{1,2}(\.\d)?$/.test(String(val).trim()) ? '' : 'Format : X, XX ou XX.X (ex : 5, 12, 12.3)';
  }

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
  };

  function field(label, key, type = 'text') {
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{label}</label>
        <input type={type} value={form[key] ?? ''} onChange={e => set(key, e.target.value)} style={inputStyle} />
      </div>
    );
  }

  function textarea(label, key) {
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{label}</label>
        <textarea value={form[key] || ''} onChange={e => set(key, e.target.value)} rows={3}
          style={{ ...inputStyle, resize: 'vertical' }} />
      </div>
    );
  }

  function checkbox(label, key) {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.88rem', marginBottom: '0.7rem' }}>
        <input type="checkbox" checked={form[key] ?? false} onChange={e => set(key, e.target.checked)} />
        {label}
      </label>
    );
  }

  async function handleSave() {
    if (!form.intitule) return setError('L\'intitulé est requis');
    const numErr = validateNumero(form.numero);
    if (numErr) { setNumeroError(numErr); return; }
    setSaving(true); setError(''); setNumeroError('');
    try {
      await apiFetch(`/formations/${formation.formation_id}`, { method: 'PUT', body: JSON.stringify(form) });
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '640px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
          Modifier — {formation.intitule?.substring(0, 50)}
        </h3>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '0.7rem', letterSpacing: '0.06em' }}>Identification</p>
        {field('Intitulé *', 'intitule')}
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Catégorie</label>
            <select value={form.categorie || ''} onChange={e => set('categorie', e.target.value)} style={inputStyle}>
              <option value="">— Choisir —</option>
              {categories.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          {field('Nom abrégé', 'nom_dossier')}
        </div>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Durée (heures)', 'duree_heures', 'number')}
          {field('Max stagiaires', 'stagiaires_max', 'number')}
        </div>
        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>N° formation * <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(X, XX ou XX.X)</span></label>
          <input type="text" value={form.numero ?? ''} onChange={e => { set('numero', e.target.value); setNumeroError(validateNumero(e.target.value)); }} style={{ ...inputStyle, borderColor: numeroError ? '#f87171' : undefined }} placeholder="ex : 5, 12, 12.3" />
          {numeroError && <p style={{ fontSize: '0.75rem', color: '#f87171', marginTop: '0.2rem' }}>{numeroError}</p>}
        </div>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Tarifs</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          {field('Coût / stagiaire HT (€)', 'cout_stagiaire_ht', 'number')}
          {field('Coût / groupe HT (€)', 'cout_groupe_ht', 'number')}
        </div>

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Contenu</p>
        {textarea('Objectifs', 'objectifs')}
        {textarea('Pré-requis', 'prerequis')}
        {textarea('Informations convocation', 'infos_complem')}
        {textarea('Mention réglementaire', 'mention_reglementaire')}
        {textarea('Note attestation', 'note_attestation')}
        {textarea('Notes internes', 'notes')}

        <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Options</p>
        <div className="grid-2" style={{ gap: '0 0.7rem' }}>
          <div>
            <div style={{ marginBottom: '0.7rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Recyclage obligatoire</label>
              <select value={form.recyclage ? 'oui' : 'non'} onChange={e => set('recyclage', e.target.value === 'oui')} style={inputStyle}>
                <option value="non">Non</option>
                <option value="oui">Oui</option>
              </select>
            </div>
            {checkbox('Adaptation PSH', 'adaptation_psh')}
            {checkbox('Formation active', 'actif')}
          </div>
          <div>{field('Période recyclage (mois)', 'periode_recyclage_mois', 'number')}</div>
        </div>
        {form.periode_recyclage_mois && (
          <div style={{ marginBottom: '0.7rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
              Formation de recyclage associée
              <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: '0.4rem' }}>(vide = cette même formation)</span>
            </label>
            <select
              value={form.formation_recyclage_id ?? ''}
              onChange={e => set('formation_recyclage_id', e.target.value ? parseInt(e.target.value) : null)}
              style={inputStyle}>
              <option value="">— Même formation (auto-référence)</option>
              {formations.filter(f => f.formation_id !== formation.formation_id).map(f => (
                <option key={f.formation_id} value={f.formation_id}>{f.numero ? `${f.numero} — ` : ''}{f.intitule}</option>
              ))}
            </select>
          </div>
        )}
        <div style={{ marginBottom: '0.7rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Modèle d'attestation</label>
          <select value={form.modele_attestation ?? 1} onChange={e => set('modele_attestation', parseInt(e.target.value))} style={inputStyle}>
            <option value={1}>Modèle 1 — Attestation de fin de formation</option>
            <option value={2}>Modèle 2 — Attestation de compétence (avec date de validité)</option>
          </select>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OngletDocuments({ formationId, token }) {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
  const [docs, setDocs] = useState([]);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const inputRef = React.useRef();

  React.useEffect(() => { charger(); }, [formationId]);

  async function charger() {
    try {
      const r = await fetch(`${API_BASE}/formations/${formationId}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setDocs(d.data || []);
    } catch (e) { console.error(e); }
  }

  function consulterDoc(doc) {
    ouvrirDocument({ titre: doc.nom_original, url: `/formations/documents/${doc.id}/download?inline=1`, nomFichier: doc.nom_original });
  }

  async function uploader(file) {
    if (!file || file.type !== 'application/pdf') {
      setErrMsg('Seuls les fichiers PDF sont acceptés'); return;
    }
    setErrMsg('');

    // Si un seul document actif existe, proposer de l'archiver
    const docsActifs = docs.filter(d => !d.archived);
    let archiveId = null;
    if (docsActifs.length === 1) {
      const ancien = docsActifs[0];
      if (window.confirm(`Un document existe déjà :\n« ${ancien.nom_original} »\n\nVoulez-vous archiver l'ancien avant d'ajouter le nouveau ?`)) {
        archiveId = ancien.id;
      }
    }

    setUploading(true);
    try {
      if (archiveId) {
        await fetch(`${API_BASE}/formations/documents/${archiveId}/archive`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ archived: true }),
        });
      }
      const fd = new FormData();
      fd.append('fichier', file);
      const r = await fetch(`${API_BASE}/formations/${formationId}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error); }
      await charger();
    } catch (e) { setErrMsg(e.message); }
    finally { setUploading(false); }
  }

  async function toggleArchive(doc) {
    try {
      await fetch(`${API_BASE}/formations/documents/${doc.id}/archive`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ archived: !doc.archived }),
      });
      await charger();
    } catch (e) { alert(e.message); }
  }

  async function supprimer(docId, nom) {
    if (!window.confirm(`Supprimer définitivement « ${nom} » ?`)) return;
    try {
      await fetch(`${API_BASE}/formations/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await charger();
    } catch (e) { alert(e.message); }
  }

  function onDrop(e) {
    e.preventDefault(); setDrag(false);
    const file = e.dataTransfer.files[0];
    if (file) uploader(file);
  }

  function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
  }

  const docsActifs = docs.filter(d => !d.archived);
  const docsArchives = docs.filter(d => d.archived);

  function renderDoc(doc, isArchived) {
    return (
      <div key={doc.id} style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.6rem 0.8rem', borderRadius: '8px',
        border: '1px solid var(--border)',
        background: isArchived ? 'transparent' : 'var(--bg-elevated)',
        marginBottom: '0.5rem',
        opacity: isArchived ? 0.55 : 1,
      }}>
        <span style={{ fontSize: '1.2rem' }}>{isArchived ? '🗃' : '📄'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.nom_original}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
            {formatSize(doc.taille)} — {isArchived ? 'archivé' : 'ajouté'} le {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
          </div>
        </div>
        {!isArchived && (
          <button onClick={() => consulterDoc(doc)}
            style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '0.8rem', fontWeight: 600, flexShrink: 0, cursor: 'pointer' }}>
            👁 Consulter
          </button>
        )}
        {!isArchived && (
          <button onClick={() => telechargerDocument({ url: `/formations/documents/${doc.id}/download`, nomFichier: doc.nom_original })} style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', background: 'var(--accent-blue)', color: '#fff', border: 'none', fontSize: '0.8rem', fontWeight: 600, flexShrink: 0, cursor: 'pointer' }}>
            ⬇ Télécharger
          </button>
        )}
        <button onClick={() => toggleArchive(doc)}
          style={{ padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>
          {isArchived ? '↩ Désarchiver' : '🗃 Archiver'}
        </button>
        <button onClick={() => supprimer(doc.id, doc.nom_original)}
          style={{ padding: '0.3rem 0.6rem', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#f87171', fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0 }}>
          🗑
        </button>
      </div>
    );
  }

  return (
    <div>
      {docs.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          {docsActifs.map(doc => renderDoc(doc, false))}
          {docsArchives.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '0.5rem', letterSpacing: '0.06em' }}>
                Archives ({docsArchives.length})
              </p>
              {docsArchives.map(doc => renderDoc(doc, true))}
            </div>
          )}
        </div>
      )}
      {docs.length === 0 && (
        <p style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', marginBottom: '1.5rem' }}>Aucun document pour cette formation.</p>
      )}

      {/* Zone de dépôt */}
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${drag ? 'var(--accent-blue)' : 'var(--border)'}`,
          borderRadius: '12px', padding: '2.5rem 1rem', textAlign: 'center',
          cursor: 'pointer', background: drag ? 'rgba(59,130,246,0.05)' : 'var(--bg-elevated)',
          transition: 'all 0.15s',
        }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📂</div>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          {uploading ? 'Téléversement en cours...' : 'Glisser-déposer un PDF ici'}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>ou cliquer pour sélectionner un fichier (PDF uniquement, 20 Mo max)</div>
        <input ref={inputRef} type="file" accept="application/pdf" style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) uploader(e.target.files[0]); e.target.value = ''; }} />
      </div>

      {errMsg && (
        <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.8rem', borderRadius: '6px', ...styleMessage(false), fontSize: '0.85rem' }}>
          {errMsg}
        </div>
      )}

    </div>
  );
}

export default function FormationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, token } = useAuth();
  const [adjFormations, setAdjFormations] = useState({ prev: null, next: null });
  const { data, loading, reload } = useApi(`/formations/${id}`);

  useEffect(() => {
    if (!id) return;
    apiFetch(`/formations/${id}/adjacent`).then(d => setAdjFormations(d)).catch(() => {});
  }, [id]);
  const [modalModif, setModalModif] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [onglet, setOnglet] = useState('sessions');

  if (loading || !data) return <Loading />;

  async function handleArchiver() {
    if (!window.confirm(`Archiver la formation "${data.intitule}" ?\nElle sera masquée des listes mais pourra être restaurée.`)) return;
    setDeleting(true);
    try {
      await apiFetch(`/formations/${id}`, { method: 'DELETE' });
      navigate('/formations');
    } catch (e) { alert(`Erreur : ${e.message}`); setDeleting(false); }
  }

  async function handleRestaurer() {
    setDeleting(true);
    try {
      await apiFetch(`/formations/${id}/restaurer`, { method: 'POST' });
      window.location.reload();
    } catch (e) { alert(`Erreur : ${e.message}`); setDeleting(false); }
  }

  const sessionCols = [
    { header: 'Session', key: 'session_id' },
    { header: 'Début', render: r => formatDate(r.date_debut) },
    { header: 'Fin', render: r => formatDate(r.date_fin) },
    { header: 'Formateur', key: 'nom_formateur' },
    { header: 'Durée', render: r => `${r.duree}h` },
    { header: 'Statut', render: r => r.annulee ? <Badge variant="badge-danger">Annulée</Badge> : <Badge variant="badge-success">Active</Badge> },
  ];

  return (
    <div className="page">
      <PageHeader
        title={data.intitule}
        subtitle={`Formation #${data.numero || data.formation_id} — ${data.categorie || ''}`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <BackButton />
            <button className="btn-secondary" onClick={() => navigate(`/formations/${adjFormations.prev}`)}
              disabled={!adjFormations.prev} style={{ opacity: adjFormations.prev ? 1 : 0.35 }}
              title={adjFormations.prev || 'Première formation'}>◀ Précédent</button>
            <button className="btn-secondary" onClick={() => navigate(`/formations/${adjFormations.next}`)}
              disabled={!adjFormations.next} style={{ opacity: adjFormations.next ? 1 : 0.35 }}
              title={adjFormations.next || 'Dernière formation'}>Suivant ▶</button>
            {hasRole('utilisateur') && (
              <>
                {data.actif === false && (
                  <span style={{ padding: '0.3rem 0.8rem', borderRadius: '20px', background: 'rgba(239,68,68,0.12)', color: '#f87171', fontSize: '0.8rem', fontWeight: 600 }}>ARCHIVÉE</span>
                )}
                <button className="btn-secondary" onClick={() => setModalModif(true)}>✏ Modifier</button>
                {data.actif !== false ? (
                  <button onClick={handleArchiver} disabled={deleting}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.5)', background: 'transparent', color: '#f87171', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {deleting ? '⏳...' : '🗑 Archiver'}
                  </button>
                ) : (
                  <button onClick={handleRestaurer} disabled={deleting}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--accent-emerald)', background: 'transparent', color: 'var(--accent-emerald)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {deleting ? '⏳...' : '↩ Restaurer'}
                  </button>
                )}
              </>
            )}
          </div>
        }
      />

      <div className="detail-grid">
        <div className="card">
          <h3 className="card-title">Détails</h3>
          <div className="info-grid">
            {data.numero && <div className="info-row"><label>N° formation</label><span style={{ fontWeight: 700 }}>{data.numero}</span></div>}
            <div className="info-row"><label>Catégorie</label><Badge variant="badge-neutral">{data.categorie}</Badge></div>
            <div className="info-row"><label>Durée</label><span>{data.duree_heures}h</span></div>
            <div className="info-row"><label>Max stagiaires</label><span>{data.stagiaires_max || '—'}</span></div>
            <div className="info-row"><label>Coût / stagiaire</label><span>{formatCurrency(data.cout_stagiaire_ht)}</span></div>
            <div className="info-row"><label>Coût / groupe</label><span>{formatCurrency(data.cout_groupe_ht)}</span></div>
            <div className="info-row"><label>Recyclage obligatoire</label><span style={{ fontWeight: 600, color: data.recyclage ? 'var(--accent-emerald)' : 'var(--text-secondary)' }}>{data.recyclage ? 'Oui' : 'Non'}</span></div>
            <div className="info-row">
              <label>Période recyclage</label>
              <span>
                {data.periode_recyclage_mois ? (
                  <>
                    {data.periode_recyclage_mois} mois
                    {!data.recyclage && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', borderRadius: '4px', padding: '0.1rem 0.35rem', verticalAlign: 'middle' }}>CONSEILLÉE</span>
                    )}
                  </>
                ) : '—'}
              </span>
            </div>
            {data.periode_recyclage_mois && (
              <div className="info-row">
                <label>Formation recyclage</label>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {data.formation_recyclage_id
                    ? <>{data.recyclage_formation_intitule || `#${data.formation_recyclage_id}`}</>
                    : <span style={{ fontStyle: 'italic', color: 'var(--text-tertiary)' }}>Cette formation (auto-référence)</span>
                  }
                </span>
              </div>
            )}
            <div className="info-row"><label>Adaptation PSH</label><span>{data.adaptation_psh ? 'Oui' : 'Non'}</span></div>
            <div className="info-row"><label>Modèle attestation</label><span>{data.modele_attestation === 2 ? 'Modèle 2 — Compétence' : 'Modèle 1 — Fin de formation'}</span></div>
            <div className="info-row"><label>Nom abrégé</label><span>{data.nom_dossier || '—'}</span></div>
            <div className="info-row"><label>Statut</label><span style={{ color: data.actif ? 'var(--accent-emerald)' : '#ef4444', fontWeight: 600 }}>{data.actif ? 'Active' : 'Inactive'}</span></div>
            <div className="info-row">
              <label>Version programme</label>
              <span style={{ fontWeight: 600 }}>
                v{data.version_programme || 1}
                {data.date_version_programme && (
                  <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '0.4rem', fontSize: '0.85em' }}>
                    — {new Date(data.date_version_programme).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="card">
          <h3 className="card-title">Objectifs</h3>
          <p className="notes-text">{data.objectifs || '—'}</p>
          <h3 className="card-title" style={{ marginTop: '1.5rem' }}>Pré-requis</h3>
          <p className="notes-text">{data.prerequis || '—'}</p>
          {data.infos_complem && <>
            <h3 className="card-title" style={{ marginTop: '1.5rem' }}>Informations convocation</h3>
            <p className="notes-text">{data.infos_complem}</p>
          </>}
          {data.mention_reglementaire && <>
            <h3 className="card-title" style={{ marginTop: '1.5rem' }}>Mention réglementaire</h3>
            <p className="notes-text">{data.mention_reglementaire}</p>
          </>}
          {data.note_attestation && <>
            <h3 className="card-title" style={{ marginTop: '1.5rem' }}>Note attestation</h3>
            <p className="notes-text">{data.note_attestation}</p>
          </>}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        {/* Barre d'onglets */}
        <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--border)', marginBottom: '1.25rem' }}>
          {[
            { key: 'sessions', label: `Sessions (${data.sessions?.length || 0})` },
            { key: 'documents', label: '📄 Documents' },
          ].map(o => (
            <button key={o.key} onClick={() => setOnglet(o.key)} style={{
              padding: '0.6rem 1.2rem', background: 'none', border: 'none',
              borderBottom: onglet === o.key ? '2px solid var(--accent-blue)' : '2px solid transparent',
              color: onglet === o.key ? 'var(--accent-blue)' : 'var(--text-secondary)',
              fontWeight: onglet === o.key ? 700 : 500, fontSize: '0.9rem',
              cursor: 'pointer', marginBottom: '-1px', transition: 'all 0.15s',
            }}>{o.label}</button>
          ))}
        </div>

        {onglet === 'sessions' && (
          <DataTable columns={sessionCols} data={data.sessions} onRowClick={r => navigate(`/sessions/${r.session_id}`)} />
        )}
        {onglet === 'documents' && (
          <OngletDocuments formationId={id} token={token} />
        )}
      </div>

      {modalModif && (
        <ModalModifier
          formation={data}
          onSave={() => { setModalModif(false); reload(); }}
          onClose={() => setModalModif(false)}
        />
      )}
    </div>
  );
}
